import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useContext, useEffect } from 'react';

import { MessagingContext } from '@sorare/wallet-shared';
import type { PromptRestoreWallet } from '@sorare/wallet-shared';

import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import {
  WalletTab,
  useWalletDrawerContext,
} from '#src/contexts/walletDrawer/index.tsx';
import { useLazyQuery } from '#src/hooks/graphql/useLazyQuery.ts';
import { useQueryString } from '#src/hooks/useQueryString.ts';

import type {
  RestoreWalletQuery,
  RestoreWalletQueryVariables,
} from './__generated__/usePromptRestoreWallet.graphql.ts';

const RESTORE_WALLET_QUERY = gql`
  query RestoreWalletQuery($id: String!) {
    currentUser {
      slug
      wallet {
        id
        privateKeyRecoveryPayload(id: $id) {
          iv
          ivTemp
          encryptionKey
          payload
        }
      }
    }
  }
` as TypedDocumentNode<RestoreWalletQuery, RestoreWalletQueryVariables>;

export const usePromptRestoreWallet = () => {
  const { sendRequest } = useContext(MessagingContext)!;
  const { currentUser } = useCurrentUserContext();
  const { showNotification } = useSnackNotificationContext();
  const { showWallet, mounted, setCurrentTab } = useWalletDrawerContext();
  const action = useQueryString('action');
  const id = useQueryString('id');

  const [query, { data, loading }] = useLazyQuery(RESTORE_WALLET_QUERY, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const doPrompt = async () => {
      if (action === 'restoreWallet' && id && !data) {
        query({ variables: { id } });
      }

      if (
        action !== 'restoreWallet' ||
        !id ||
        loading ||
        !data ||
        !mounted ||
        !currentUser
      ) {
        return;
      }

      const privateKeyRecoveryPayload =
        data?.currentUser?.wallet?.privateKeyRecoveryPayload;

      if (privateKeyRecoveryPayload) {
        // Switch to the restore tab before opening the drawer: on an
        // unconfirmed device the HOME tab would otherwise show the device
        // confirmation block for a frame.
        setCurrentTab(WalletTab.RESTORE_WALLET);
        showWallet();
        await sendRequest<PromptRestoreWallet>('promptRestoreWallet', {
          privateKeyRecoveryPayload,
        });
      } else {
        showNotification(
          'nullSorarePrivateKeyRecovery',
          {},
          { autoHideDuration: null }
        );
      }
    };

    doPrompt();
  }, [
    action,
    currentUser,
    sendRequest,
    showNotification,
    showWallet,
    mounted,
    setCurrentTab,
    query,
    id,
    data,
    loading,
  ]);
};
